import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { VerifiedBadge } from '../components/app/VerifiedBadge';
import { BarChart3, Medal, Zap } from 'lucide-react';
import { toast } from 'sonner';

export default function LeaderboardPage() {
  const { t, user, refreshUser } = useApp();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('weekly');
  const [board, setBoard] = useState([]);
  const [anon, setAnon] = useState(user?.anonymous_mode || false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard?period=${period}`).then((r) => { setBoard(r.data.board); setLoading(false); }).catch(() => setLoading(false));
  }, [period]);

  const toggleAnon = async (v) => {
    setAnon(v);
    try {
      await api.put('/users/me', { anonymous_mode: v });
      refreshUser();
      toast.success(v ? 'Anonymous mode ON — your name is hidden on public boards' : 'Anonymous mode OFF');
      const r = await api.get(`/leaderboard?period=${period}`);
      setBoard(r.data.board);
    } catch { setAnon(!v); }
  };

  const medal = (rank) => rank === 1 ? 'text-[hsl(38,92%,55%)]' : rank === 2 ? 'text-slate-300' : rank === 3 ? 'text-orange-400' : 'text-muted-foreground';

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4">
      <div className="flex items-center gap-2 mb-5">
        <BarChart3 size={20} className="text-primary" />
        <h1 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{t('leaderboard')}</h1>
      </div>

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Tabs value={period} onValueChange={setPeriod} data-testid="leaderboard-period-tabs">
          <TabsList>
            <TabsTrigger data-testid="leaderboard-weekly-tab" value="weekly">{t('weekly')}</TabsTrigger>
            <TabsTrigger data-testid="leaderboard-monthly-tab" value="monthly">{t('monthly')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          {t('anonymousMode')}
          <Switch data-testid="leaderboard-anonymous-toggle" checked={anon} onCheckedChange={toggleAnon} />
        </label>
      </div>

      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">{t('loading')}</div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden elev-1" data-testid="leaderboard-table">
          {board.map((row) => (
            <div key={row.rank}
              className={`flex items-center gap-3 px-4 py-3 border-b border-border/50 last:border-0 ${row.is_me ? 'bg-primary/5' : ''}`}
              data-testid="leaderboard-row">
              <div className="w-8 flex justify-center">
                {row.rank <= 3 ? <Medal size={18} className={medal(row.rank)} /> : <span className="text-sm text-muted-foreground font-medium">{row.rank}</span>}
              </div>
              <Avatar className={`w-9 h-9 ${row.user_id ? 'cursor-pointer' : ''}`} onClick={() => row.user_id && navigate(`/profile/${row.user_id}`)}>
                <AvatarImage src={row.avatar} />
                <AvatarFallback>{row.username?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium truncate">{row.full_name}</p>
                  {row.verified && <VerifiedBadge size={13} />}
                  {row.is_me && <span className="text-[10px] text-primary">(you)</span>}
                </div>
                <p className="text-xs text-muted-foreground">@{row.username}</p>
              </div>
              <div className="flex items-center gap-1 text-primary font-semibold text-sm">
                <Zap size={13} /> {row.xp.toLocaleString()}
              </div>
            </div>
          ))}
          {board.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No rankings yet this {period === 'weekly' ? 'week' : 'month'}</p>}
        </div>
      )}
    </div>
  );
}
